import { useState, useEffect, useRef, memo, useCallback } from "react";
import { Dumbbell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

// Muscle group to color/icon mapping
const muscleMap: Record<string, { color: string; emoji: string }> = {
  peito: { color: "from-red-500/20 to-orange-500/20", emoji: "🫁" },
  costas: { color: "from-blue-500/20 to-cyan-500/20", emoji: "🔙" },
  ombro: { color: "from-purple-500/20 to-pink-500/20", emoji: "💪" },
  bíceps: { color: "from-emerald-500/20 to-teal-500/20", emoji: "💪" },
  tríceps: { color: "from-amber-500/20 to-yellow-500/20", emoji: "💪" },
  quadríceps: { color: "from-blue-600/20 to-indigo-500/20", emoji: "🦵" },
  posterior_coxa: { color: "from-violet-500/20 to-purple-500/20", emoji: "🦵" },
  glúteos: { color: "from-pink-500/20 to-rose-500/20", emoji: "🍑" },
  panturrilha: { color: "from-teal-500/20 to-green-500/20", emoji: "🦵" },
  core: { color: "from-orange-500/20 to-red-500/20", emoji: "🎯" },
  trapézio: { color: "from-sky-500/20 to-blue-500/20", emoji: "🔙" },
  antebraço: { color: "from-lime-500/20 to-emerald-500/20", emoji: "💪" },
  adutores: { color: "from-fuchsia-500/20 to-pink-500/20", emoji: "🦵" },
  abdutores: { color: "from-fuchsia-500/20 to-pink-500/20", emoji: "🦵" },
  corpo_todo: { color: "from-cyan-500/20 to-teal-500/20", emoji: "🏋️" },
  cardio: { color: "from-rose-500/20 to-red-500/20", emoji: "❤️" },
};

export function detectMuscleGroup(exerciseName: string): { color: string; emoji: string } {
  const name = exerciseName.toLowerCase();
  if (name.includes("supino") || name.includes("crucifixo") || name.includes("peck") || name.includes("peito")) return muscleMap.peito;
  if (name.includes("remada") || name.includes("pulldown") || name.includes("puxada") || name.includes("barra fixa") || name.includes("costas")) return muscleMap.costas;
  if (name.includes("desenvolvimento") || name.includes("elevação lateral") || name.includes("ombro") || name.includes("deltoid")) return muscleMap.ombro;
  if (name.includes("rosca") && !name.includes("inversa")) return muscleMap.bíceps;
  if (name.includes("tríceps") || name.includes("triceps") || name.includes("testa") || name.includes("francês") || name.includes("pushdown")) return muscleMap.tríceps;
  if (name.includes("agachamento") || name.includes("leg press") || name.includes("extensora") || name.includes("hack") || name.includes("quadríceps")) return muscleMap.quadríceps;
  if (name.includes("stiff") || name.includes("mesa flexora") || name.includes("flexora") || name.includes("posterior") || name.includes("terra")) return muscleMap.posterior_coxa;
  if (name.includes("glúteo") || name.includes("gluteo") || name.includes("hip thrust") || name.includes("búlgaro")) return muscleMap.glúteos;
  if (name.includes("panturrilha") || name.includes("gêmeos") || name.includes("gemeos")) return muscleMap.panturrilha;
  if (name.includes("abdominal") || name.includes("prancha") || name.includes("crunch") || name.includes("oblíquo") || name.includes("core")) return muscleMap.core;
  if (name.includes("encolhimento") || name.includes("trapézio")) return muscleMap.trapézio;
  return { color: "from-primary/20 to-primary/10", emoji: "🏋️" };
}

/** Extract YouTube video ID from various URL formats */
export function extractYouTubeId(url?: string | null): string | null {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

function deriveFrame2FromUrl(url?: string | null): string | null {
  if (!url) return null;
  const match = url.match(/\/0\.(jpg|jpeg|png|webp)(\?.*)?$/i);
  if (!match) return null;
  const ext = match[1];
  const query = match[2] || "";
  return url.replace(/\/0\.(jpg|jpeg|png|webp)(\?.*)?$/i, `/1.${ext}${query}`);
}

export interface ExerciseLibraryData {
  id: string;
  name: string;
  media_url: string | null;
  thumbnail_url: string | null;
  primary_muscle_group: string;
  coaching_cues: string[] | null;
  common_errors: string[] | null;
  description: string | null;
  difficulty_level: string;
  equipment: string[] | null;
  contraindications: string[] | null;
}

// Simple in-memory cache
const exerciseCache = new Map<string, ExerciseLibraryData | null>();
const inflightLookups = new Map<string, Promise<ExerciseLibraryData | null>>();

/** Normalize string for comparison: lowercase, remove accents, extra spaces */
function normalizeForMatch(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9 ]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Score how well a candidate name matches the search name (higher = better) */
function nameMatchScore(search: string, candidate: string): number {
  const sNorm = normalizeForMatch(search);
  const cNorm = normalizeForMatch(candidate);

  // Exact normalized match
  if (sNorm === cNorm) return 100;

  // Exact match ignoring spaces
  if (sNorm.replace(/ /g, "") === cNorm.replace(/ /g, "")) return 95;

  const sWords = sNorm.split(" ");
  const cWords = cNorm.split(" ");

  // Count shared words
  const shared = sWords.filter((w) => cWords.some((cw) => cw.includes(w) || w.includes(cw)));
  const sharedRatio = shared.length / Math.max(sWords.length, cWords.length);

  // Full containment with similar length is good
  if (cNorm.includes(sNorm)) return 80 + Math.round(10 * (sNorm.length / cNorm.length));
  if (sNorm.includes(cNorm)) return 80 + Math.round(10 * (cNorm.length / sNorm.length));

  // Word overlap score
  if (sharedRatio >= 0.5) return Math.round(50 + sharedRatio * 30);

  // Partial containment
  if (shared.length >= 1) return 20 + shared.length * 10;

  return 0;
}

const NAME_STOP_WORDS = new Set(["de", "da", "do", "na", "no", "com", "e", "a", "o", "em"]);

function getCoreWords(value: string): string[] {
  return normalizeForMatch(value)
    .split(" ")
    .filter((w) => w.length >= 3 && !NAME_STOP_WORDS.has(w));
}

/** Priority: GIF > video > storage > static image */
function mediaScore(u?: string | null): number {
  if (!u) return 0;
  if (/\.gif(\?.*)?$/i.test(u) || u.includes("exercisedb")) return 4;
  if (/\.(mp4|webm|mov)(\?.*)?$/i.test(u)) return 3;
  if (u.includes("supabase.co/storage")) return 2;
  return 1;
}

export function useExerciseFromLibrary(exerciseName: string) {
  const [exercise, setExercise] = useState<ExerciseLibraryData | null>(
    exerciseCache.get(exerciseName) ?? null
  );
  const [loaded, setLoaded] = useState(exerciseCache.has(exerciseName));

  useEffect(() => {
    if (exerciseCache.has(exerciseName)) {
      setExercise(exerciseCache.get(exerciseName) ?? null);
      setLoaded(true);
      return;
    }

    let cancelled = false;
    let promise = inflightLookups.get(exerciseName);
    if (!promise) {
      promise = (async () => {
        try {
          const trimmed = exerciseName.trim();
          const selectCols =
            "id, name, media_url, thumbnail_url, primary_muscle_group, coaching_cues, common_errors, description, difficulty_level, equipment, contraindications";

          // 1. Exact match
          let { data } = await supabase
            .from("exercises")
            .select(selectCols)
            .eq("name", trimmed)
            .limit(1);

          if (data?.length) {
            const result = data[0] as ExerciseLibraryData;
            exerciseCache.set(exerciseName, result);
            return result;
          }

          // 2. Fuzzy search — get many candidates, then rank by name similarity
          const res = await supabase
            .from("exercises")
            .select(selectCols)
            .ilike("name", `%${trimmed}%`)
            .limit(20);
          data = res.data;

          if (!data?.length) {
            // 3. Try first meaningful word (≥4 chars)
            const words = normalizeForMatch(trimmed).split(" ").filter((w) => w.length >= 4);
            if (words.length > 0) {
              const res2 = await supabase
                .from("exercises")
                .select(selectCols)
                .ilike("name", `%${words[0]}%`)
                .limit(20);
              data = res2.data;
            }
          }

          if (!data?.length) {
            exerciseCache.set(exerciseName, null);
            return null;
          }

          const searchCoreWords = getCoreWords(trimmed);

          // Rank candidates: name similarity first, then media quality as tiebreaker
          const ranked = data
            .map((ex) => {
              const candidateCoreWords = getCoreWords(ex.name);
              const sharedCore = searchCoreWords.filter((sw) =>
                candidateCoreWords.some((cw) => cw === sw || cw.includes(sw) || sw.includes(cw))
              );
              const coreCoverage = searchCoreWords.length > 0 ? sharedCore.length / searchCoreWords.length : 0;

              return {
                ex: ex as ExerciseLibraryData,
                nameScore: nameMatchScore(trimmed, ex.name),
                coreCoverage,
                mediaScore: mediaScore(ex.media_url),
                searchCoreCount: searchCoreWords.length,
              };
            })
            .filter((r) => {
              // For short names, require stronger score to avoid mismatches
              if (r.searchCoreCount <= 1) return r.nameScore >= 70;

              // General rule: strong semantic match required
              return (
                r.nameScore >= 70 ||
                (r.nameScore >= 55 && r.coreCoverage >= 0.75) ||
                (r.nameScore >= 45 && r.coreCoverage === 1)
              );
            })
            .sort((a, b) => {
              // Primary: name match quality
              if (a.nameScore !== b.nameScore) return b.nameScore - a.nameScore;
              // Secondary: core word coverage
              if (a.coreCoverage !== b.coreCoverage) return b.coreCoverage - a.coreCoverage;
              // Tertiary: media quality
              return b.mediaScore - a.mediaScore;
            });

          const result = ranked[0]?.ex || null;
          exerciseCache.set(exerciseName, result);
          return result;
        } catch {
          exerciseCache.set(exerciseName, null);
          return null;
        } finally {
          inflightLookups.delete(exerciseName);
        }
      })();
      inflightLookups.set(exerciseName, promise);
    }

    promise.then((result) => {
      if (!cancelled) {
        setExercise(result);
        setLoaded(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [exerciseName]);

  return { exercise, loaded };
}

interface Props {
  exerciseName: string;
  videoUrl?: string | null;
  /** Second frame URL for animation (from student-data edge function) */
  frame2Url?: string | null;
  compact?: boolean;
  onClick?: (e?: React.MouseEvent) => void;
}

/** Check if URL points to a video file */
function isVideoUrl(url?: string | null): boolean {
  if (!url) return false;
  return /\.(mp4|webm|mov)(\?.*)?$/i.test(url);
}

const EXERCISE_MEDIA_BASE = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/exercise-media`;

function normalizeExerciseSlug(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getStorageFallbackUrls(exerciseName: string): string[] {
  const slug = normalizeExerciseSlug(exerciseName || "");
  if (!slug) return [];

  return [
    `${EXERCISE_MEDIA_BASE}/${slug}.gif`,
    `${EXERCISE_MEDIA_BASE}/${slug}_barra.gif`,
    `${EXERCISE_MEDIA_BASE}/${slug}_halteres.gif`,
    `${EXERCISE_MEDIA_BASE}/global/${slug}.gif`,
    `${EXERCISE_MEDIA_BASE}/global/${slug}_1.gif`,
  ];
}

function pickFirstWorkingUrl(
  candidates: Array<string | null | undefined>,
  failedUrls: Set<string>
): string | null {
  for (const candidate of candidates) {
    if (candidate && !failedUrls.has(candidate)) return candidate;
  }
  return null;
}

/**
 * Animated exercise media component.
 * Renders native <video> for MP4, crossfade for dual-frame JPGs, YouTube embed, or GIF.
 */
function ExerciseMediaInner({ exerciseName, videoUrl, frame2Url, compact = false, onClick }: Props) {
  const [currentFrame, setCurrentFrame] = useState(0);
  const [bothLoaded, setBothLoaded] = useState(false);
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const [videoReady, setVideoReady] = useState(false);
  const loadedCount = useRef(0);
  const { exercise } = useExerciseFromLibrary(exerciseName);
  const muscle = exercise?.primary_muscle_group
    ? muscleMap[exercise.primary_muscle_group] || detectMuscleGroup(exerciseName)
    : detectMuscleGroup(exerciseName);

  const markUrlAsFailed = useCallback((url?: string | null) => {
    if (!url) return;
    setFailedUrls((prev) => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);

  useEffect(() => {
    setFailedUrls(new Set());
    setVideoReady(false);
  }, [exerciseName, videoUrl, frame2Url, exercise?.media_url, exercise?.thumbnail_url]);

  // Priority: GIF (ExerciseDB) > Video (MP4) > Storage fallback > Static
  const storageFallbackUrls = getStorageFallbackUrls(exerciseName);
  const isGifUrl = (u?: string | null) => u ? (/\.gif(\?.*)?$/i.test(u) || u.includes('exercisedb')) : false;
  const gifCandidates = [exercise?.media_url, videoUrl].filter((u) => u && isGifUrl(u));
  const videoCandidates = [videoUrl, exercise?.media_url].filter(
    (u) => u && isVideoUrl(u) && !isGifUrl(u)
  );
  const nonVideoCandidates = [exercise?.media_url, videoUrl, ...storageFallbackUrls].filter(
    (u) => u && !isGifUrl(u) && !isVideoUrl(u)
  );
  const primaryUrl = pickFirstWorkingUrl(
    [...gifCandidates, ...videoCandidates, ...nonVideoCandidates],
    failedUrls
  );

  // Check if it's a YouTube URL
  const youtubeId = extractYouTubeId(primaryUrl);

  // Check if URL is a native video (MP4/WebM)
  const isVideo = !youtubeId && isVideoUrl(primaryUrl);

  // Check if primary URL is already a real GIF
  const isRealGif = !youtubeId && !isVideo && primaryUrl ? /\.gif(\?.*)?$/i.test(primaryUrl) : false;

  // Poster for video to avoid blank boxes while loading
  const videoPoster = pickFirstWorkingUrl(
    [frame2Url, exercise?.thumbnail_url, ...storageFallbackUrls],
    failedUrls
  ) || undefined;

  // For 2-frame animation: second frame
  const frame2 = isRealGif || isVideo
    ? null
    : pickFirstWorkingUrl(
        [
          frame2Url,
          exercise?.thumbnail_url,
          deriveFrame2FromUrl(videoUrl),
          deriveFrame2FromUrl(exercise?.media_url),
        ],
        failedUrls
      );

  const hasAnimation = !isRealGif && !isVideo && !!primaryUrl && !!frame2 && primaryUrl !== frame2;

  // Track when both frames are loaded for smooth start
  const handleFrameLoad = useCallback(() => {
    loadedCount.current++;
    if (loadedCount.current >= 2) {
      setBothLoaded(true);
    }
  }, []);

  // Reset load state when URLs change
  useEffect(() => {
    loadedCount.current = 0;
    setBothLoaded(false);
  }, [primaryUrl, frame2]);

  // If an MP4 stalls and never becomes playable, fallback to next media candidate
  useEffect(() => {
    if (!isVideo || !primaryUrl || videoReady) return;
    const timeout = setTimeout(() => {
      markUrlAsFailed(primaryUrl);
    }, 5000);
    return () => clearTimeout(timeout);
  }, [isVideo, primaryUrl, videoReady, markUrlAsFailed]);

  // 3-state cycle with variable hold times for organic feel
  const holdTimes = [1200, 800, 1000];
  useEffect(() => {
    if (!hasAnimation || !bothLoaded) return;
    let timeout: ReturnType<typeof setTimeout>;
    const tick = () => {
      setCurrentFrame((f) => {
        const next = (f + 1) % 3;
        timeout = setTimeout(tick, holdTimes[next]);
        return next;
      });
    };
    timeout = setTimeout(tick, holdTimes[0]);
    return () => clearTimeout(timeout);
  }, [hasAnimation, bothLoaded]);

  // ── Native video (MP4/WebM) ──
  if (isVideo && primaryUrl) {
    return (
      <div
        className={`rounded-xl overflow-hidden bg-card border border-border/30 cursor-pointer relative ${
          compact ? "w-16 h-16 shrink-0" : "w-full aspect-video"
        }`}
        onClick={(e) => onClick?.(e)}
      >
        <video
          src={primaryUrl}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-contain bg-muted/20"
          onLoadedData={() => setVideoReady(true)}
          onCanPlay={() => setVideoReady(true)}
          onError={() => markUrlAsFailed(primaryUrl)}
          preload={compact ? "none" : "metadata"}
          poster={videoPoster}
        />
      </div>
    );
  }

  // ── YouTube embed ──
  if (youtubeId) {
    const youtubeWatchUrl = `https://www.youtube.com/watch?v=${youtubeId}`;

    return (
      <div
        className={`rounded-xl overflow-hidden bg-card border border-border/30 relative ${
          compact ? "w-16 h-16 shrink-0" : "w-full aspect-video"
        }`}
        onClick={compact ? (e) => onClick?.(e) : undefined}
      >
        {compact ? (
          <img
            src={`https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`}
            alt={exerciseName}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <>
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
              title={exerciseName}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
            <a
              href={youtubeWatchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute right-2 top-2 z-10 rounded-md bg-background/85 px-2 py-1 text-[10px] font-semibold text-foreground backdrop-blur-sm"
            >
              Abrir no YouTube
            </a>
          </>
        )}
      </div>
    );
  }

  // ── Real GIF ──
  if (isRealGif && primaryUrl) {
    return (
      <div
        className={`rounded-xl overflow-hidden bg-card border border-border/30 cursor-pointer relative ${
          compact ? "w-16 h-16 shrink-0" : "w-full aspect-video"
        }`}
        onClick={(e) => onClick?.(e)}
      >
        <img
          src={primaryUrl}
          alt={exerciseName}
          className="w-full h-full object-contain bg-white/5"
          onError={() => markUrlAsFailed(primaryUrl)}
          loading="lazy"
        />
      </div>
    );
  }

  // ── 2-frame crossfade animation (JPG pairs) ──
  if (hasAnimation) {
    const frameATransform =
      currentFrame === 0
        ? "scale(1.015) translateY(0.2%)"
        : currentFrame === 2
        ? "scale(1.025) translateY(-0.2%)"
        : "scale(1) translateY(0%)";

    const frameBTransform =
      currentFrame === 1
        ? "scale(1.02) translateY(-0.15%)"
        : "scale(1) translateY(0.1%)";

    return (
      <div
        className={`rounded-xl overflow-hidden bg-card border border-border/30 cursor-pointer relative ${
          compact ? "w-16 h-16 shrink-0" : "w-full aspect-video"
        }`}
        onClick={(e) => onClick?.(e)}
      >
        <img
          src={primaryUrl!}
          alt={exerciseName}
          className="absolute inset-0 w-full h-full object-contain bg-white/5"
          style={{
            opacity: currentFrame === 1 ? 0 : 1,
            transform: frameATransform,
            transition: "opacity 500ms ease-in-out, transform 1s ease-in-out",
          }}
          onLoad={handleFrameLoad}
          onError={() => markUrlAsFailed(primaryUrl)}
          loading="lazy"
        />
        <img
          src={frame2!}
          alt=""
          className="absolute inset-0 w-full h-full object-contain bg-white/5"
          style={{
            opacity: currentFrame === 1 ? 1 : 0,
            transform: frameBTransform,
            transition: "opacity 500ms ease-in-out, transform 1s ease-in-out",
          }}
          onLoad={handleFrameLoad}
          onError={() => markUrlAsFailed(frame2)}
          aria-hidden="true"
          loading="lazy"
        />
        {!compact && bothLoaded && (
          <div className="absolute bottom-1.5 right-1.5 flex gap-0.5">
            {[0, 1, 2].map((i) => (
              <span key={i} className={`h-1 w-1 rounded-full transition-all duration-500 ${currentFrame === i ? 'bg-primary/70 scale-110' : 'bg-muted-foreground/15 scale-100'}`} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Static image ──
  if (primaryUrl) {
    return (
      <div
        className={`rounded-xl overflow-hidden bg-card border border-border/30 cursor-pointer relative ${
          compact ? "w-16 h-16 shrink-0" : "w-full aspect-video"
        }`}
        onClick={(e) => onClick?.(e)}
      >
        <img
          src={primaryUrl}
          alt={exerciseName}
          className="w-full h-full object-contain bg-white/5"
          onError={() => markUrlAsFailed(primaryUrl)}
          loading="lazy"
        />
      </div>
    );
  }

  // ── Fallback: icon ──
  return (
    <div
      className={`rounded-xl bg-muted/30 border border-border/30 flex items-center justify-center cursor-pointer ${
        compact ? "w-14 h-14 shrink-0" : "w-full h-24"
      }`}
      onClick={(e) => onClick?.(e)}
    >
      <Dumbbell className={`text-muted-foreground/40 ${compact ? "h-5 w-5" : "h-8 w-8"}`} />
    </div>
  );
}

const ExerciseMedia = memo(ExerciseMediaInner);
export default ExerciseMedia;
